import AuthentificationService from '@/services/authentification'
import * as urls from '@/consts/urls'
import { USER_TYPE_USER, USER_TYPE_SYSTEM_ADMIN } from '@/consts/enums'

// simplifies route config
const fullscreen = true,
  anonymous = true,
  business = true,
  waitForScrollSignal = true

/*
meta: {
  fullscreen: false, // If true, hides main app navigation
  anonymous: false, // If true, does not require login
  business: false, // If true, a business must be selected,
  usertype: null, // set to USER_TYPE_USER or USER_TYPE_SYSTEM_ADMIN to restrict page by user type
}
*/

// NOTE: On pourrait potentiellement splitter la config de routes en sous-groupes logiques et les combiner ici avec [...someRoutes, ...someOtherRoutes]

export default [
  {
    name: urls.URL_ROOT,
    path: '/',
    redirect() {
      const isLoggedIn = AuthentificationService.getUserIsLogged()

      if (!isLoggedIn) {
        return { name: urls.URL_ACCOUNT_LOGIN }
      }

      const userType = AuthentificationService.getUserType()

      switch (userType) {
        case USER_TYPE_SYSTEM_ADMIN:
          return {
            name: urls.URL_BUSINESS_LIST
          }
        default:
          return {
            name: urls.URL_USER_DASHBOARD
          }
      }
    },
    meta: {
      anonymous
    }
  },

  ///////////////////////////
  // Account
  ///////////////////////////
  {
    name: urls.URL_ACCOUNT_LOGIN,
    path: '/login',
    component: () => import('@/components/pages/account/login.vue'),
    meta: {
      fullscreen,
      anonymous
    }
  },
  {
    name: urls.URL_ACCOUNT_FORGOT_PASSWORD,
    path: '/forgot-password',
    component: () => import('@/components/pages/account/forgot-password.vue'),
    meta: {
      fullscreen,
      anonymous
    }
  },
  {
    name: urls.URL_ACCOUNT_RESET_PASSWORD,
    path: '/reset-password',
    component: () => import('@/components/pages/account/reset-password.vue'),
    meta: {
      fullscreen,
      anonymous
    }
  },

  {
    name: urls.URL_ACCOUNT_CHANGE_USER_EMAIL,
    path: '/change-user-email',
    component: () => import('@/components/pages/account/change-user-email.vue'),
    meta: {
      fullscreen
    }
  },

  {
    name: urls.URL_ACCOUNT_SUBSCRIBE,
    path: '/subscribe',
    component: () => import('@/components/pages/account/subscribe-user.vue'),
    meta: {
      fullscreen,
      anonymous
    }
  },
  {
    name: urls.URL_ACCOUNT_USER_PROFILE,
    path: '/profile',
    component: () => import('@/components/pages/account/profile.vue')
  },
  {
    name: urls.URL_ACCOUNT_APPLICANT_FIRST_CONNECTION,
    path: '/first-login',
    component: () => import('@/components/pages/account/first-connection.vue'),
    meta: {
      fullscreen,
      usertype: USER_TYPE_USER
    }
  },
  {
    name: urls.URL_ACCOUNT_BUSINESS_SELECT,
    path: '/select-business',
    component: () => import('@/components/pages/account/business-selection.vue'),
    meta: {
      fullscreen,
      usertype: USER_TYPE_USER
    }
  },
  {
    name: urls.URL_ACCOUNTS_SEARCH,
    path: '/search-accounts',
    component: () => import('@/components/pages/account/search-accounts.vue'),
    meta: {
      usertype: USER_TYPE_SYSTEM_ADMIN
    }
  },

  ///////////////////////////
  // Dashboards
  ///////////////////////////
  {
    name: urls.URL_USER_DASHBOARD,
    path: '/user-dashboard',
    component: () => import('@/components/pages/dashboards/user-dashboard.vue'),
    meta: {
      business,
      usertype: USER_TYPE_USER
    }
  },

  ///////////////////////////
  // Businesses
  ///////////////////////////
  {
    name: urls.URL_BUSINESS_ADD,
    path: '/add-business',
    component: () => import('@/components/pages/businesses/add-business.vue'),
    meta: {
      usertype: USER_TYPE_SYSTEM_ADMIN
    }
  },
  {
    name: urls.URL_BUSINESS_LIST,
    path: '/businesses',
    component: () => import('@/components/pages/businesses/list.vue'),
    meta: {
      usertype: USER_TYPE_SYSTEM_ADMIN
    }
  },
  {
    name: urls.URL_BUSINESS_DETAIL,
    path: '/manage-business',
    component: () => import('@/components/pages/businesses/detail.vue'),
    meta: {
      business,
      usertype: USER_TYPE_USER
    }
  },
  {
    name: urls.URL_BUSINESS_ADD_USER,
    path: '/manage-business/add-user',
    component: () => import('@/components/pages/businesses/add-user.vue'),
    meta: {
      business,
      usertype: USER_TYPE_USER
    }
  },
  {
    name: urls.URL_BUSINESS_EDIT_USER,
    path: '/manage-business/user/:id',
    component: () => import('@/components/pages/businesses/edit-user.vue'),
    meta: {
      business,
      usertype: USER_TYPE_USER
    }
  },

  ///////////////////////////
  // Projects
  ///////////////////////////
  {
    name: urls.URL_PROJECT_LIST,
    path: '/projects',
    component: () => import('@/components/pages/projects/projects-list.vue'),
    meta: {
      business,
      usertype: USER_TYPE_USER
    }
  },
  {
    name: urls.URL_PROJECT_ADD,
    path: '/add-project',
    component: () => import('@/components/pages/projects/add-project.vue'),
    meta: {
      business,
      usertype: USER_TYPE_USER
    }
  },

  // Redirection pour mauvais format d'URL qui était utilisé dans les courriels
  {
    path: '/project/*',
    redirect: to => {
      return '/projects/' + to.params.pathMatch
    }
  },

  ///////////////////////////
  // Compliance
  ///////////////////////////
  {
    name: urls.URL_GENERAL_COMPLIANCE,
    path: '/general-compliance',
    component: () => import('@/components/pages/compliance/general-compliance.vue'),
    meta: {
      business,
      waitForScrollSignal,
      usertype: USER_TYPE_USER
    }
  },
  {
    name: urls.URL_PROJECT_COMPLIANCE,
    path: '/projects/:projectId',
    component: () => import('@/components/pages/compliance/project-compliance.vue'),
    meta: {
      business,
      waitForScrollSignal,
      usertype: USER_TYPE_USER
    }
  },
  {
    name: urls.URL_INDICATOR_GENERAL,
    path: '/general-compliance/:id',
    component: () => import('@/components/pages/compliance/indicator.vue'),
    meta: {
      business,
      waitForScrollSignal,
      scrollOffset: { y: 65 },
      usertype: USER_TYPE_USER
    }
  },
  {
    name: urls.URL_INDICATOR_PROJECT,
    path: '/projects/:projectId/:id',
    component: () => import('@/components/pages/compliance/indicator.vue'),
    meta: {
      business,
      waitForScrollSignal,
      scrollOffset: { y: 65 },
      usertype: USER_TYPE_USER
    }
  },

  // Redirection pour mauvais format d'URL qui était utilisé dans les courriels
  {
    path: '/general-conformity/*',
    redirect: to => {
      return '/general-compliance/' + to.params.pathMatch
    }
  },

  ///////////////////////////
  // Manual
  ///////////////////////////
  {
    name: urls.URL_MANUAL_GENERATION,
    path: '/manual-generation',
    component: () => import('@/components/pages/manual/manual.vue'),
    meta: {
      business,
      usertype: USER_TYPE_USER
    }
  },

  ///////////////////////////
  // Tasks
  ///////////////////////////
  {
    name: urls.URL_PENDING_TASKS,
    path: '/tasks',
    component: () => import('@/components/pages/tasks/pending-tasks.vue'),
    meta: {
      business,
      waitForScrollSignal,
      usertype: USER_TYPE_USER
    }
  },

  ///////////////////////////
  // Style guide
  ///////////////////////////
  {
    name: urls.URL_STYLE_GUIDE,
    path: '/style-guide',
    component: () => import('@/components/pages/content/style-guide.vue'),
    meta: {
      business,
      usertype: USER_TYPE_USER
    }
  },

  ///////////////////////////
  // Content
  ///////////////////////////
  {
    name: urls.URL_STATIC_PAGES,
    path: '/pages/:uid',
    component: () => import('@/components/pages/content/static-page.vue'),
    meta: {
      business,
      usertype: USER_TYPE_USER
    }
  },
  {
    path: '*',
    component: () => import('@/components/pages/content/error-404.vue')
  },

  ///////////////////////////
  // Questionnaires
  ///////////////////////////
  {
    name: urls.URL_QUESTIONNAIRE_ESG_ADMIN,
    path: '/questionnaires/esg/configure',
    component: () => import('@/components/pages/questionnaire/questionnaire-admin.vue'),
    meta: {
      usertype: USER_TYPE_SYSTEM_ADMIN
    }
  },
  {
    name: urls.URL_QUESTIONNAIRE_ESG,
    path: '/questionnaires/esg',
    component: () => import('@/components/pages/questionnaire/questionnaire.vue'),
    meta: {
      business,
      usertype: USER_TYPE_USER
    }
  }
]
